import {
  type ReactNode,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import * as motion from "motion/react-client";
import { CodeContainer } from "../../base/CodeContainer";
import { GreenSenseiIcon, InfoOutlineIcon } from "../../base/Icon";
import { Button } from "../../base/Button";
import { Tooltip } from "../../base/Tooltip";
import { OGPLinkCard } from "../../base/OGPLinkCard";
import { MarkdownContent, type GlossaryEntry } from "../../base/MarkdownContent";
import { LessonGlossaryLinks } from "../LessonGlossaryLinks";
import { useHintSteps } from "./useHintSteps";
import { HintLadder } from "./HintLadder";

// Tab Component
export interface TabItem {
  label: string;
  id: string;
}

/**
 * 札の列の見た目。
 * - pill 既定。角の丸い枠に入れて、欄の上の真ん中に置く (レッスンの頁はこのまま)
 * - flush 欄の上端にぴったり付けた普通の札の列。左寄せで、今の札は太字と下線。
 *   右のエディターのファイルの札の列 (高さ 32px) とそろえる
 */
export type ResumeTabsVariant = "pill" | "flush";

interface TabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  large?: boolean;
}

function Tabs({ tabs, activeTab, onTabChange, large = false }: TabsProps) {
  return (
    <div className="bg-second border border-second rounded-[12px] p-1 flex gap-4 items-center">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={`
            flex min-h-11 shrink-0 items-center justify-center px-2 py-2 rounded-lg border-0 cursor-pointer
            font-sans leading-none whitespace-nowrap ${large ? "text-[15px]" : "text-[15px] md:text-xs"}
            ${
              activeTab === tab.id
                ? "bg-third font-bold text-sub"
                : "bg-transparent font-normal text-sub"
            }
          `}
          onClick={() => onTabChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* 欄の上端に付く札の列。ResumeArea の padding (p-4) を負の margin で打ち消して縁まで寄せる。
   PC では本文を送っても札は上に残す (sticky は器の padding の内側で止まるので -top-4 で縁に合わせる)。
   スマホは頁全体が動くので残さない */
function FlushTabs({ tabs, activeTab, onTabChange, large = false }: TabsProps) {
  return (
    <div
      data-resume-tabs="flush"
      className="-mx-4 -mt-4 flex h-11 shrink-0 items-stretch self-stretch overflow-x-auto rounded-t-lg bg-second md:sticky md:-top-4 md:z-20 md:h-8"
    >
      {tabs.map((tab) => {
        const active = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            aria-pressed={active}
            className={`relative flex shrink-0 cursor-pointer items-center justify-center border-0 px-4 font-sans leading-none whitespace-nowrap focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary ${
              large ? "text-[15px]" : "text-[15px] md:text-xs"
            } ${
              active
                ? "bg-main font-bold text-main after:absolute after:inset-x-0 after:bottom-0 after:h-0.5 after:bg-primary"
                : "bg-transparent font-normal text-sub hover:text-main"
            }`}
            onClick={() => onTabChange(tab.id)}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

// Section Navigation Component
export interface SectionNavItem {
  label: string;
  id: string;
  /**
   * 押したときの動き。渡すとアンカーへのスクロールではなくこちらを呼ぶ。
   * スライドのように本文の中に無いものをタブから開くために使う
   */
  onSelect?: () => void;
  /** 先頭に置くアイコン。react-feather などの要素をそのまま渡す */
  icon?: ReactNode;
}

interface SectionNavProps {
  sections: SectionNavItem[];
  activeSection?: string;
}

function SectionNav({ sections, activeSection }: SectionNavProps) {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="bg-second rounded-[12px] p-1 flex gap-1 md:gap-4 items-center overflow-x-auto">
      {sections.map((section) => (
        <button
          key={section.id}
          className={`
            flex min-h-11 shrink-0 items-center justify-center gap-1 px-1.5 md:px-2 py-2 rounded-lg border-0 cursor-pointer
            font-sans text-[15px] md:text-xs leading-none whitespace-nowrap transition-colors
            ${
              activeSection === section.id
                ? "bg-third font-bold text-sub"
                : "bg-transparent font-normal text-sub"
            }
          `}
          onClick={() =>
            section.onSelect ? section.onSelect() : scrollToSection(section.id)
          }
        >
          {section.icon}
          {section.label}
        </button>
      ))}
    </div>
  );
}

// Heading Components
interface HeadingProps {
  children: ReactNode;
  level?: 1 | 2 | 3;
  className?: string;
}

function Heading({ children, level = 1, className = "" }: HeadingProps) {
  const headingClasses: Record<number, string> = {
    1: "font-sans text-2xl font-bold leading-none text-main",
    2: "font-sans text-xl font-bold leading-none text-main",
    3: "font-sans text-base font-bold leading-none text-main",
  };

  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <Tag className={`${headingClasses[level]} ${className}`.trim()}>
      {children}
    </Tag>
  );
}

// ヒントの文はバッククォートの対と ** の対だけを描く。
// # * :: や HTML は意味を持たせず文字のまま出す (React の要素だけで組む)
const HINT_INLINE = /(`[^`\n]+`|\*\*[^*\n]+\*\*)/g;

export function HintInlineText({ text }: { text: string }) {
  return (
    <>
      {text.split(HINT_INLINE).map((part, i) => {
        if (part.length > 2 && part.startsWith("`") && part.endsWith("`")) {
          // 短いコードは途中で折らない (「title: "」が空きで折れていた、2026-10-28 の点検)。長いものだけ折って幅からはみ出さない
          const short = part.length - 2 <= 24;
          return (
            <code
              key={i}
              className={`px-1.5 py-1 rounded max-sm:text-[0.9375rem] text-[14px] font-mono bg-primary/10 text-primary ${short ? "whitespace-nowrap" : "break-words"}`}
            >
              {part.slice(1, -1)}
            </code>
          );
        }
        if (part.length > 4 && part.startsWith("**") && part.endsWith("**")) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </>
  );
}


// ヒントの段 (1 つずつ開く)
interface HintStepsProps {
  hints: string[];
  id?: string;
  /** Optional reviewed visual shown inside the hint section, before the rows. */
  visual?: ReactNode;
  /**
   * 道場かどうか。開き方は全部のレッスンで同じ 1 つずつで、道場は未開封クリアの案内を足す。
   * 並びは渡された順のまま (道場は方針、構文、部分解の順で書かれている)
   */
  gated?: boolean;
  /** 何段目まで開いたかを外に知らせる。段位の材料 (未開封クリア) に使う */
  onOpenedCountChange?: (count: number) => void;
}

/*
ヒントは段のはしごに付箋を貼る形 (10/28 代表が案 D を選んだ。前の青い枠の釦と白い札の並びは
「ダサい」と言われた)。振る舞いは useHintSteps が持つ (1 つずつ開き、開いた物は積み、閉じない)。

見た目は HintLadder が持つ (クラウドエディターの板と同じ部品)。
最後の 1 つは答えに近いことが多いので、開く前から黄の丸と札で分かるようにし、付箋も黄にする (nearLast)
*/
function HintSteps({
  hints,
  id,
  visual,
  gated = false,
  onOpenedCountChange,
}: HintStepsProps) {
  const { opened, openNext, lastItemRef } = useHintSteps(hints, onOpenedCountChange);
  return (
    <HintLadder
      id={id}
      hints={hints}
      opened={opened}
      onOpenNext={openNext}
      lead={
        gated
          ? [`付箋は ${hints.length} 枚。`, "めくらずに解けると", "未開封クリアです"]
          : [`付箋は ${hints.length} 枚。`, "下の段ほど", "答えに近づきます"]
      }
      visual={visual}
      nearLast
      renderText={(text) => <HintInlineText text={text} />}
      lastItemRef={lastItemRef}
    />
  );
}

// Reference Link Interface
export interface ReferenceLink {
  title: string;
  description?: string;
  url: string;
  imageUrl?: string;
}

// Reference Links Section Component
function ReferenceLinksSection({
  links,
  id,
  LinkComponent,
}: {
  links: ReferenceLink[];
  id?: string;
  LinkComponent?: ComponentType<any>;
}) {
  if (links.length === 0) return null;

  return (
    <div id={id} className="space-y-4">
      <h3 className="font-sans text-xl font-bold leading-none text-main flex items-center gap-2">
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9.16699 4.16797C9.62708 4.16814 10 4.54085 10 5.00098C10 5.46111 9.62708 5.83381 9.16699 5.83398H4.16699C3.94598 5.83398 3.73343 5.92184 3.57715 6.07812C3.42087 6.23441 3.33301 6.44695 3.33301 6.66797V15.834C3.33301 16.055 3.42087 16.2675 3.57715 16.4238C3.73343 16.5801 3.94598 16.668 4.16699 16.668H13.333C13.554 16.668 13.7666 16.5801 13.9229 16.4238C14.0791 16.2675 14.167 16.055 14.167 15.834V10.834C14.1672 10.3739 14.5399 10.001 15 10.001C15.4601 10.001 15.8328 10.3739 15.833 10.834V15.834C15.833 16.497 15.5694 17.1327 15.1006 17.6016C14.6317 18.0704 13.996 18.334 13.333 18.334H4.16699C3.50395 18.334 2.86825 18.0704 2.39941 17.6016C1.93057 17.1327 1.66699 16.497 1.66699 15.834V6.66797C1.66699 6.00493 1.93057 5.36923 2.39941 4.90039C2.86826 4.43155 3.50395 4.16797 4.16699 4.16797H9.16699ZM17.5 1.66797C17.5419 1.66797 17.5828 1.67178 17.623 1.67773C17.637 1.67981 17.6512 1.68081 17.665 1.68359C17.6796 1.68651 17.6938 1.69067 17.708 1.69434C17.7184 1.697 17.729 1.69907 17.7393 1.70215C17.7525 1.70611 17.7654 1.71122 17.7783 1.71582C17.8155 1.72898 17.8523 1.74409 17.8877 1.7627L17.9658 1.80957C17.9753 1.81595 17.984 1.82333 17.9932 1.83008C18.0266 1.85469 18.0586 1.88188 18.0889 1.91211C18.1188 1.94201 18.1455 1.97384 18.1699 2.00684C18.1782 2.0181 18.1866 2.02933 18.1943 2.04102C18.2322 2.09808 18.2618 2.15883 18.2842 2.22168C18.2888 2.23465 18.2939 2.24748 18.2979 2.26074C18.3093 2.29886 18.3164 2.33784 18.3223 2.37695C18.3283 2.41751 18.333 2.45873 18.333 2.50098V7.50098C18.333 7.96121 17.9602 8.33398 17.5 8.33398C17.0398 8.33398 16.667 7.96121 16.667 7.50098V4.51172L8.92285 12.2568C8.59742 12.5823 8.06958 12.5823 7.74414 12.2568C7.4187 11.9314 7.4187 11.4036 7.74414 11.0781L15.4893 3.33398H12.5C12.0398 3.33398 11.667 2.96121 11.667 2.50098C11.667 2.04074 12.0398 1.66797 12.5 1.66797H17.5Z"
            fill="#05B45B"
          />
        </svg>
        参考リンク
      </h3>
      <div className="space-y-3">
        {links.map((link, i) => (
          <OGPLinkCard key={`${link.url}-${i}`} url={link.url} LinkComponent={LinkComponent} />
        ))}
      </div>
    </div>
  );
}

// FAQ Accordion Component
interface FaqItem {
  question: string;
  answer: string;
}

interface FaqAccordionProps {
  items: FaqItem[];
  id?: string;
}

function FaqAccordion({ items, id }: FaqAccordionProps) {
  if (items.length === 0) return null;

  return (
    <div id={id} className="space-y-4">
      <h3 className="font-sans text-xl font-bold leading-none text-main">
        よくある質問
      </h3>
      <div className="bg-second rounded-lg p-4 flex flex-col gap-4">
        {items.map((item, i) => (
          <div key={i} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <svg
                className="w-5 h-5 shrink-0 mt-0.5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1D6FD3"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <span className="max-sm:text-[0.9375rem] text-sm font-bold" style={{ color: "#1D6FD3" }}>
                {item.question}
              </span>
            </div>
            <p className="max-sm:text-[0.9375rem] text-sm text-main leading-[1.8] pl-7 -mt-0.5 m-0">
              {item.answer}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Content Section
export interface ContentSection {
  type:
    | "heading"
    | "text"
    | "code"
    | "markdown"
    | "hints"
    | "faq"
    | "links"
    | "node";
  level?: 1 | 2 | 3;
  content: string | ReactNode;
  hints?: string[]; // hintsタイプ用
  /** 道場の段階ゲート。前のヒントを開くまで次を開けなくする (hintsタイプ用) */
  hintsGated?: boolean;
  /** 何段目まで開いたかを外に知らせる (hintsタイプ用) */
  onHintsOpenedCountChange?: (count: number) => void;
  faqItems?: FaqItem[]; // faqタイプ用
  referenceLinks?: ReferenceLink[]; // linksタイプ用
  filename?: string;
  onCopy?: () => void;
  onAiHelp?: () => void;
  id?: string; // セクションナビゲーション用のID
  icon?: ReactNode; // ヘッダー用アイコン
  tooltip?: string; // ツールチップ説明
}

// Main ResumeArea Component
export interface ResumeAreaProps {
  /**
   * タブの配列
   */
  tabs?: TabItem[];
  /** 札の字を PC でも 15px にする (札の字の決まりの大きい段)。既定は PC で 12px */
  largeTabs?: boolean;
  /** 札の列の見た目。既定は pill (今のまま)。flush は欄の上端に付く札の列 */
  tabsVariant?: ResumeTabsVariant;

  /**
   * 初期アクティブタブID
   */
  defaultActiveTab?: string;

  /**
   * タブ変更時のコールバック
   */
  onTabChange?: (tabId: string) => void;

  /**
   * セクションナビゲーション用のアイテム
   */
  sectionNav?: SectionNavItem[];

  /**
   * コンテンツセクションの配列
   */
  sections: ContentSection[];

  /**
   * グラデーションを表示するか
   */
  showGradient?: boolean;

  /**
   * 追加のクラス名
   */
  className?: string;

  /**
   * コードブロックのAIヘルプボタンクリック時のコールバック
   */
  onAiHelp?: (code: string) => void;

  /**
   * テキスト選択時の質問コールバック
   */
  onTextSelectionQuestion?: (selectedText: string, question: string) => void;

  /**
   * 参考リンクのURL配列
   */
  referenceLinks?: string[];

  /**
   * 内部リンク用のLinkコンポーネント（React RouterのLinkなど）
   */
  LinkComponent?: ComponentType<any>;

  /**
   * 用語ハイライト辞書。markdown / text セクション内の用語に
   * ホバー説明 + DB slug 一致時は /glossary/:slug への内部リンクを付与する。
   * デフォルト undefined = 用語ハイライト無効。
   */
  glossary?: GlossaryEntry[];

  /**
   * 本文のいちばん下 (参考リンクと用語の後ろ) に置く物。スクロールの中に入る
   */
  footer?: ReactNode;
}

/**
 * 本文を選択したときに出る「もっと詳しく」の吹き出しを出すかどうか。
 *
 * 一旦止めている。読んでいる途中で本文をなぞっただけで、緑の大きな札が
 * 本文に重なって出るため。戻すときはここを true にするだけでよく、
 * 中の実装 (handleMouseUp・ポップオーバー) は残してある。
 */
const SELECTION_ASK_ENABLED = false;

export function ResumeArea({
  tabs = [],
  largeTabs = false,
  tabsVariant = "pill",
  defaultActiveTab,
  onTabChange,
  sectionNav = [],
  sections = [],
  showGradient = true,
  className = "",
  onAiHelp,
  onTextSelectionQuestion,
  referenceLinks,
  LinkComponent,
  glossary,
  footer,
}: ResumeAreaProps) {
  const [activeTab, setActiveTab] = useState(
    defaultActiveTab || tabs[0]?.id || ""
  );
  const [activeSection, setActiveSection] = useState<string>("");
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // テキスト選択ポップオーバー用の状態
  const [selectionPopover, setSelectionPopover] = useState<{
    visible: boolean;
    position: { top: number; left: number };
    selectedText: string;
    question: string;
  }>({
    visible: false,
    position: { top: 0, left: 0 },
    selectedText: "",
    question: "",
  });
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  // テキスト選択時のハンドラー
  const handleMouseUp = useCallback(() => {
    if (!SELECTION_ASK_ENABLED) return;
    if (!onTextSelectionQuestion) return;

    const selection = window.getSelection();
    const text = selection?.toString().trim();

    if (text && text.length > 0 && containerRef.current) {
      const range = selection?.getRangeAt(0);
      if (range) {
        const rect = range.getBoundingClientRect();

        setSelectionPopover({
          visible: true,
          position: {
            top: rect.top + window.scrollY - 50,
            left: rect.left + rect.width / 2,
          },
          selectedText: text,
          question: "",
        });
      }
    }
  }, [onTextSelectionQuestion]);

  // ポップオーバー外クリック時のハンドラー
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
      setSelectionPopover((prev) => ({
        ...prev,
        visible: false,
        question: "",
      }));
    }
  }, []);

  // 質問送信ハンドラー
  const handleSelectionSubmit = useCallback(() => {
    if (selectionPopover.selectedText) {
      onTextSelectionQuestion?.(
        selectionPopover.selectedText,
        selectionPopover.question.trim() || "もっと詳しく教えてください"
      );
      setSelectionPopover((prev) => ({
        ...prev,
        visible: false,
        question: "",
      }));
      window.getSelection()?.removeAllRanges();
    }
  }, [
    selectionPopover.selectedText,
    selectionPopover.question,
    onTextSelectionQuestion,
  ]);

  // キーボードハンドラー
  const handleSelectionKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSelectionSubmit();
      } else if (e.key === "Escape") {
        setSelectionPopover((prev) => ({
          ...prev,
          visible: false,
          question: "",
        }));
      }
    },
    [handleSelectionSubmit]
  );

  // テキスト選択イベントリスナー
  useEffect(() => {
    if (!SELECTION_ASK_ENABLED) return;
    if (!onTextSelectionQuestion) return;

    const container = containerRef.current;
    if (container) {
      container.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      if (container) {
        container.removeEventListener("mouseup", handleMouseUp);
      }
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onTextSelectionQuestion, handleMouseUp, handleClickOutside]);

  // スクロール位置を監視してアクティブセクションを更新
  useEffect(() => {
    if (sectionNav.length === 0) return;

    const container = containerRef.current;
    if (!container) return;

    // セクション要素をキャッシュ（DOM検索を1回だけに）
    const sectionElements = sectionNav
      .map((nav) => ({
        id: nav.id,
        element: document.getElementById(nav.id),
      }))
      .filter((item) => item.element !== null) as Array<{
      id: string;
      element: HTMLElement;
    }>;

    if (sectionElements.length === 0) return;

    let rafId: number | null = null;
    let lastKnownScrollPosition = 0;
    let ticking = false;

    const updateActiveSection = (scrollPosition: number) => {
      const position = scrollPosition + 100; // オフセット調整

      // 現在のスクロール位置にあるセクションを見つける
      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const { id, element } = sectionElements[i];
        if (element.offsetTop <= position) {
          setActiveSection((current) => (current !== id ? id : current));
          return;
        }
      }

      // どのセクションにも該当しない場合は最初のセクション
      setActiveSection((current) =>
        current !== sectionElements[0].id ? sectionElements[0].id : current
      );
    };

    const handleScroll = () => {
      lastKnownScrollPosition = container.scrollTop;

      // スクロール位置が最上部かどうかをチェック
      if (lastKnownScrollPosition <= 10) {
        setIsScrolledFromTop(false);
      } else {
        setIsScrolledFromTop(true);
      }

      if (!ticking) {
        rafId = requestAnimationFrame(() => {
          updateActiveSection(lastKnownScrollPosition);
          ticking = false;
        });
        ticking = true;
      }
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    // 初回実行
    updateActiveSection(container.scrollTop);

    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [sectionNav]);

  const renderSection = (section: ContentSection, index: number) => {
    switch (section.type) {
      case "heading":
        const headingProps = section.id ? { id: section.id } : {};
        return (
          <div key={index} {...headingProps}>
            <Heading level={section.level || 2}>
              <span className="inline-flex items-center gap-2">
                {section.icon && (
                  <span className="flex-shrink-0">{section.icon}</span>
                )}
                {section.content}
                {section.tooltip && (
                  <Tooltip content={section.tooltip} position="top-center">
                    <InfoOutlineIcon className="w-4 h-4 text-sub cursor-help" />
                  </Tooltip>
                )}
              </span>
            </Heading>
          </div>
        );
      case "text":
        // テキストにMarkdown構文が含まれているかチェック
        const textContent =
          typeof section.content === "string" ? section.content : "";
        const hasMarkdown =
          /^#{1,6}\s|^\*\*|```|^-\s|^\d+\.\s|^\*\s|^\|.+\|$/m.test(textContent);

        if (hasMarkdown) {
          return (
            <div key={index}>
              <MarkdownContent content={textContent} glossary={glossary} />
            </div>
          );
        }

        return (
          <p
            key={index}
            className="font-[Inter,'Noto_Sans_JP',sans-serif] max-sm:text-[0.9375rem] text-sm font-normal leading-[1.8] text-main whitespace-pre-wrap"
          >
            {section.content}
          </p>
        );
      case "code":
        return (
          <CodeContainer
            key={index}
            filename={section.filename || "code"}
            code={typeof section.content === "string" ? section.content : ""}
            onCopy={section.onCopy}
            onAiHelp={section.onAiHelp}
          />
        );
      case "markdown":
        return (
          <div key={index}>
            <MarkdownContent
              content={
                typeof section.content === "string" ? section.content : ""
              }
              glossary={glossary}
            />
          </div>
        );
      case "hints":
        return (
          <HintSteps
            key={index}
            hints={section.hints || []}
            id={section.id}
            visual={
              typeof section.content === "string" ? undefined : section.content
            }
            gated={section.hintsGated}
            onOpenedCountChange={section.onHintsOpenedCountChange}
          />
        );
      case "faq":
        return (
          <FaqAccordion
            key={index}
            items={section.faqItems || []}
            id={section.id}
          />
        );
      case "links":
        return (
          <ReferenceLinksSection
            key={index}
            links={section.referenceLinks || []}
            id={section.id}
            LinkComponent={LinkComponent}
          />
        );
      case "node":
        // 任意の ReactNode をそのままレンダーする (TL;DR 等のカスタムブロック用)
        return <div key={index}>{section.content}</div>;
      default:
        return null;
    }
  };

  return (
    <div
      ref={containerRef}
      className={`bg-main py-4 px-4 rounded-lg flex flex-col gap-8 items-center relative w-full h-full box-border overflow-y-auto ${className}`.trim()}
    >
      {tabs.length > 0 &&
        (tabsVariant === "flush" ? (
          <FlushTabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} large={largeTabs} />
        ) : (
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} large={largeTabs} />
        ))}
      <div className="flex flex-col gap-6 max-w-[672px] w-full pb-60 relative">
        {sectionNav.length > 0 && (
          <motion.div
            className="sticky top-0 z-10 bg-main"
            style={{
              left: isScrolledFromTop ? "-1rem" : "0",
              right: isScrolledFromTop ? "-1rem" : "0",
            }}
            animate={{
              y: isScrolledFromTop ? -16 : 0,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
          >
            <SectionNav sections={sectionNav} activeSection={activeSection} />
          </motion.div>
        )}
        <div className="flex flex-col gap-6 w-full">
          {sections.map((section, index) => renderSection(section, index))}
          {/* 参考リンクセクション */}
          {referenceLinks && referenceLinks.length > 0 && (
            <div id="reference-links" className="space-y-4">
              <h3 className="font-sans text-xl font-bold leading-none text-main flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-[#05b45b]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                参考リンク
              </h3>
              <div className="space-y-3">
                {referenceLinks.map((url, i) => (
                  <OGPLinkCard key={`${url}-${i}`} url={url} LinkComponent={LinkComponent} />
                ))}
              </div>
            </div>
          )}

          {/* このレッスンに出てくる用語。SSR される /glossary への内部リンク */}
          <LessonGlossaryLinks
            content={sections
              .filter((s) => s.type === "text" || s.type === "markdown")
              .map((s) => (typeof s.content === "string" ? s.content : ""))
              .join("\n\n")}
            glossary={glossary}
          />
          {footer}
        </div>
      </div>
      {showGradient && (
        <div className="absolute bottom-0 left-0 right-0 h-[67px] bg-gradient-to-b from-transparent to-main pointer-events-none" />
      )}

      {/* テキスト選択ポップオーバー */}
      {SELECTION_ASK_ENABLED &&
        selectionPopover.visible &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={popoverRef}
            className="fixed z-[9999] transform -translate-x-1/2"
            style={{
              top: `${selectionPopover.position.top}px`,
              left: `${selectionPopover.position.left}px`,
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            <div className="bg-white border-2 border-primary rounded-full shadow-lg flex items-center gap-2 px-3 py-2 min-w-[200px]">
              <GreenSenseiIcon variant="smile" size="M" />
              <input
                ref={inputRef}
                type="text"
                value={selectionPopover.question}
                onChange={(e) =>
                  setSelectionPopover((prev) => ({
                    ...prev,
                    question: e.target.value,
                  }))
                }
                onKeyDown={handleSelectionKeyDown}
                placeholder="他にはどのような"
                className="flex-1 bg-transparent outline-none max-sm:text-[0.9375rem] text-sm text-gray-700 placeholder-gray-400 min-w-0"
              />
              <Button
                variant="primary"
                size="sm"
                rounded
                onClick={handleSelectionSubmit}
              >
                もっと詳しく
              </Button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
